import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import PhoneInput from "react-phone-number-input/input";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import ReactCountryFlag from "react-country-flag";
import { useForm } from "react-hook-form";
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../services";
import VerificationInput from "react-verification-input";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    setError: setFieldError,
    clearErrors,
  } = useForm({
    mode: "onChange",
  });

  const phoneValue = watch("phone");

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Validate Ghana phone number
  const validatePhoneNumber = (phone) => {
    if (!phone) {
      return "Phone number is required";
    }

    try {
      if (phone.startsWith("+233")) {
        const phoneNumber = parsePhoneNumber(phone);
        if (
          phoneNumber &&
          phoneNumber.country === "GH" &&
          isValidPhoneNumber(phone)
        ) {
          return true;
        }
      } else if (phone.startsWith("0") && phone.length === 10) {
        const ghanaPhoneRegex = /^0[2-5][0-9]{8}$/;
        if (ghanaPhoneRegex.test(phone)) {
          return true;
        }
      }
      return "Please enter a valid phone number";
    } catch (error) {
      return "Invalid phone number format";
    }
  };

  // Format phone for API
  const formatPhoneForAPI = (phone) => {
    if (phone?.startsWith("+233")) {
      return "0" + phone.substring(4);
    }
    return phone;
  };

  // Handle phone input change
  const handlePhoneChange = (value) => {
    setValue("phone", value);
    if (value) {
      const validation = validatePhoneNumber(value);
      if (validation === true) {
        clearErrors("phone");
      } else {
        setFieldError("phone", { type: "manual", message: validation });
      }
    } else {
      clearErrors("phone");
    }
  };

  // Handle PIN change
  const handlePinChange = (value) => {
    setPin(value);
    if (value.length === 4) {
      clearErrors("pin");
    } else if (value.length > 0) {
      setFieldError("pin", { type: "manual", message: "PIN must be 4 digits" });
    } else {
      clearErrors("pin");
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError("");

    const phoneValidation = validatePhoneNumber(data.phone);
    if (phoneValidation !== true) {
      setFieldError("phone", { type: "manual", message: phoneValidation });
      setLoading(false);
      return;
    }

    if (pin.length !== 4) {
      setFieldError("pin", { type: "manual", message: "PIN must be 4 digits" });
      setLoading(false);
      return;
    }

    try {
      const formattedPhone = formatPhoneForAPI(data.phone);

      const response = await authService.login({
        phone: formattedPhone,
        pin: pin,
      });

      if (response.status === "success") {
        login(response.data.tokens, response.data.user);
        navigate("/dashboard", { replace: true });
      } else {
        setError(response.message || "Login failed");
      }
    } catch (err) {
      console.error("Login error:", err);

      if (err.response?.status === 401) {
        setError("Invalid phone number or PIN");
      } else if (err.response?.status === 403) {
        setError("Account is suspended. Please contact support.");
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Login failed. Please check your connection and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Desktop Left Side - Hidden on mobile */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{ background: "#FFD700" }}
      >
        <div className="relative z-10 flex flex-col justify-start items-start text-black p-12 w-full">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-1 text-black">
              Cinivest{" "}
              <b className="text-white  rounded-tl-3xl  bg-black pb-0 px-2">
                Systems
              </b>{" "}
              <small className="text-sm">v1.0</small>
            </h1>
            <p className="text-xl text-black max-w-md font-light">
              Your trusted partner for data bundle sales
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 right-0">
          <img
            src="/customers.png"
            alt="Happy customers"
            className="h-80 w-auto object-contain"
            style={{
              filter: "drop-shadow(0 0 0 transparent)",
              background: "transparent",
            }}
          />
        </div>
      </div>

      {/* Mobile & Desktop Right Side */}
      <div className="flex-1 lg:flex lg:flex-col lg:justify-center lg:py-12 lg:px-20 xl:px-24">
        {/* Mobile: Full-screen background with parallax effect */}
        <div className="lg:hidden h-screen relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 overflow-hidden">
          {/* Background Man Image - Positioned to show head above card */}
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src="/man.png"
              alt="Happy customer"
              className="h-full w-auto object-contain opacity-90"
              style={{
                maxHeight: "100vh",
                transform: "translateY(-60px)",
              }}
            />
          </div>

          {/* Content Overlay */}
          <div className="relative z-10 h-full flex flex-col">
            {/* Top Header - Minimal */}
            <div className="pt-5 pb-2 px-6 ">
              <h1 className="text-lg font-bold text-black">
                Cinivest{" "}
                <span className="text-white  text-bold shadow-2xl  py-1 shadow-black pl-2   pr-4 underline underline-offset-8 decoration-black decoration-1">
                  Systems
                </span>
              </h1>
            </div>

            {/* Spacer to push content down */}
            <div className="flex-1"></div>

            {/* Login Card - Edge to edge with padding */}
            <div className="pb-0 px-2">
              <div className="bg-white/95 backdrop-blur-lg rounded-t-[90px] p-8 shadow-2xl border-t border-white/20 mx-0">
                {/* Form Title */}
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Welcome Back
                  </h2>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-sm text-red-600 text-center font-medium">
                      {error}
                    </p>
                  </div>
                )}

                <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                  {/* Phone Number */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <ReactCountryFlag
                          countryCode="GH"
                          svg
                          style={{ width: "1.2em", height: "1.2em" }}
                        />
                      </div>
                      <PhoneInput
                        country="GH"
                        value={phoneValue}
                        onChange={handlePhoneChange}
                        disabled={loading}
                        inputMode="numeric"
                        className="appearance-none block w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200 transition duration-200 ease-in-out disabled:bg-gray-50 disabled:cursor-not-allowed"
                        placeholder="024 512 3456"
                      />
                    </div>
                    {errors.phone && (
                      <p className="mt-1 text-xs text-red-600 font-medium">
                        {errors.phone.message}
                      </p>
                    )}
                  </div>

                  {/* PIN - Mobile */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-semibold text-gray-700">
                        PIN Code
                      </label>
                      <button
                        type="button"
                        disabled={loading}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none transition ease-in-out duration-150 disabled:cursor-not-allowed p-1"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>

                    <div className="w-full flex justify-center">
                      <VerificationInput
                        value={pin}
                        onChange={handlePinChange}
                        length={4}
                        autoFocus
                        passwordMode={!showPassword}
                        placeholder=""
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          autoComplete: "one-time-code",
                          placeholder: "",
                        }}
                        removeDefaultStyles
                        classNames={{
                          container: "flex justify-center gap-3",
                          character: "pin-input-mobile",
                          characterInactive: "pin-inactive",
                          characterSelected: "pin-selected",
                          characterFilled: "pin-filled",
                        }}
                      />
                    </div>

                    {errors.pin && (
                      <p className="mt-2 text-xs text-red-600 text-center font-medium">
                        {errors.pin.message}
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-3">
                    <button
                      type="submit"
                      disabled={loading || errors.phone || errors.pin}
                      className="w-full flex justify-center items-center py-3 px-6 border-transparent text-base font-bold rounded-xl text-black shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-yellow-200 transition duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600"
                    >
                      {loading ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                          Signing in...
                        </div>
                      ) : (
                        "Sign In"
                      )}
                    </button>
                  </div>
                </form>

                {/* Register Link */}
                <div className="text-center pt-4 pb-2">
                  <p className="text-sm text-gray-600">
                    Don't have an account?{" "}
                    <Link
                      to="/register"
                      className="font-bold hover:underline transition duration-150 ease-in-out text-yellow-600"
                    >
                      Create account
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Form Container */}
        <div className="hidden lg:block mx-auto w-full max-w-sm lg:w-96">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Welcome back</h2>
            <p className="mt-2 text-sm text-gray-600">
              Sign in to your account
            </p>
          </div>

          <div className="mt-8">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* Phone Number */}
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <ReactCountryFlag
                      countryCode="GH"
                      svg
                      style={{
                        width: "1.5em",
                        height: "1.5em",
                      }}
                    />
                  </div>
                  <PhoneInput
                    country="GH"
                    value={phoneValue}
                    onChange={handlePhoneChange}
                    disabled={loading}
                    inputMode="numeric"
                    className="appearance-none block w-full pl-12 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200 transition duration-200 ease-in-out sm:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                    placeholder="024 512 3456"
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    {errors.phone.message}
                  </p>
                )}
              </div>

              {/* PIN - Desktop */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    PIN Code
                  </label>
                  <button
                    type="button"
                    disabled={loading}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none transition ease-in-out duration-150 disabled:cursor-not-allowed"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>

                <div className="w-full flex justify-center">
                  <VerificationInput
                    value={pin}
                    onChange={handlePinChange}
                    length={4}
                    autoFocus
                    passwordMode={!showPassword}
                    placeholder=""
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      autoComplete: "one-time-code",
                      placeholder: "",
                    }}
                    removeDefaultStyles
                    classNames={{
                      container: "flex justify-center gap-4",
                      character: "pin-input-desktop",
                      characterInactive: "pin-inactive",
                      characterSelected: "pin-selected",
                      characterFilled: "pin-filled",
                    }}
                  />
                </div>

                {errors.pin && (
                  <p className="mt-2 text-sm text-red-600 text-center">
                    {errors.pin.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={loading || errors.phone || errors.pin}
                  className="w-full flex justify-center items-center py-3 px-4  border-transparent text-base font-medium rounded-lg text-black shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 transition duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-yellow-400 to-yellow-600"
                  style={{ backgroundColor: "#FFD700" }}
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                      Signing in...
                    </div>
                  ) : (
                    "Sign in"
                  )}
                </button>
              </div>

              {/* Register Link */}
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{" "}
                  <Link
                    to="/register"
                    className="font-medium hover:underline transition duration-150 ease-in-out text-yellow-600"
                  >
                    Create account
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Custom CSS for PIN inputs */}
      <style jsx>{`
        .pin-input-mobile {
          width: 48px !important;
          height: 48px !important;
          font-size: 20px !important;
          font-weight: bold !important;
          text-align: center !important;
          background-color: #f9fafb !important;
          border: 2px solid #e5e7eb !important;
          border-radius: 12px !important;
          outline: none !important;
          transition: all 0.2s ease-in-out !important;
          caret-color: #1f2937 !important;
        }

        .pin-input-desktop {
          width: 56px !important;
          height: 56px !important;
          font-size: 24px !important;
          font-weight: bold !important;
          text-align: center !important;
          background-color: #f9fafb !important;
          border: 2px solid #e5e7eb !important;
          border-radius: 12px !important;
          outline: none !important;
          transition: all 0.2s ease-in-out !important;
          caret-color: #1f2937 !important;
        }

        .pin-inactive {
          border-color: #e5e7eb !important;
          background-color: #f9fafb !important;
        }

        .pin-selected {
          border-color: #fbbf24 !important;
          background-color: white !important;
          box-shadow: 0 0 0 2px rgba(251, 191, 36, 0.2) !important;
        }

        .pin-filled {
          border-color: #fbbf24 !important;
          background-color: white !important;
        }

        .pin-input-mobile:focus,
        .pin-input-desktop:focus {
          border-color: #fbbf24 !important;
          background-color: white !important;
          box-shadow: 0 0 0 2px rgba(251, 191, 36, 0.2) !important;
        }

        .pin-input-mobile:disabled,
        .pin-input-desktop:disabled {
          cursor: not-allowed !important;
          opacity: 0.5 !important;
        }

        /* Remove any placeholder dots/characters */
        .pin-input-mobile::placeholder,
        .pin-input-desktop::placeholder {
          color: transparent !important;
          opacity: 0 !important;
        }

        .pin-input-mobile::-webkit-input-placeholder,
        .pin-input-desktop::-webkit-input-placeholder {
          color: transparent !important;
          opacity: 0 !important;
        }

        .pin-input-mobile::-moz-placeholder,
        .pin-input-desktop::-moz-placeholder {
          color: transparent !important;
          opacity: 0 !important;
        }

        .pin-input-mobile:-ms-input-placeholder,
        .pin-input-desktop:-ms-input-placeholder {
          color: transparent !important;
          opacity: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default Login;
